/**
 * Versioned, curated jurisdiction boundary adapters.
 *
 * ETJ coverage is never inferred from municipal limits. An entry may only
 * declare `complete` after an authoritative ETJ dataset has been reviewed.
 */
export interface JurisdictionArcgisPolygonSource {
  providerId: string;
  queryUrl: string;
  nameFields: readonly string[];
  idFields: readonly string[];
  sourceLabel: string;
  sourceUrl: string;
}

export interface JurisdictionArcgisLayer extends JurisdictionArcgisPolygonSource {
  layerKind: 'city-limit' | 'etj';
}

export interface JurisdictionRegistryEntry {
  id: string;
  version: number;
  stateFips: string;
  countyFips: string;
  stateCode: string;
  countyName: string;
  bbox: { west: number; south: number; east: number; north: number };
  layers: readonly JurisdictionArcgisLayer[];
  etjCoverage: 'complete' | 'partial' | 'not-published';
  datasetEffectiveDate?: string;
  lastReviewedAt: string;
  authorityReferences: readonly string[];
}

export interface VerifiedJurisdictionFips {
  stateFips: string;
  countyFips: string;
}

export const CALCASIEU_CITY_LIMITS_QUERY =
  'https://lak-dc-arcgis2.cppj.net/arcgis/rest/services/HubLayers/City_Limits/FeatureServer/0/query';

/**
 * Pilot adapter for Redhead / Calcasieu Parish. The official service only
 * publishes city limits, so ETJ remains explicitly not published.
 */
export const JURISDICTION_REGISTRY: readonly JurisdictionRegistryEntry[] = [
  {
    id: 'us-la-calcasieu-22019-v2',
    version: 2,
    stateFips: '22',
    countyFips: '019',
    stateCode: 'LA',
    countyName: 'Calcasieu Parish',
    bbox: { west: -94.05, south: 29.72, east: -92.95, north: 30.62 },
    layers: [
      {
        providerId: 'calcasieu-parish-city-limits',
        queryUrl: CALCASIEU_CITY_LIMITS_QUERY,
        layerKind: 'city-limit',
        nameFields: ['CITY'],
        idFields: ['OBJECTID'],
        sourceLabel: 'Calcasieu Parish official city limits',
        sourceUrl:
          'https://lak-dc-arcgis2.cppj.net/arcgis/rest/services/HubLayers/City_Limits/FeatureServer',
      },
    ],
    etjCoverage: 'not-published',
    // The current Hub item's modification date is not a boundary effective
    // date. Do not carry the retired layer's 2021 data vintage onto this source.
    lastReviewedAt: '2026-09-07',
    authorityReferences: [
      'https://gis.calcasieu.gov/',
      'https://www.arcgis.com/home/item.html?id=b5e39ad46933437d90e811510e61fb2e',
      'Official Calcasieu Parish GIS city-limit polygons. No authoritative parish-wide ETJ polygon layer was published at review time.',
      'https://www.calcasieu.gov/services/planning-development/planning-and-zoning',
      'https://www.legis.la.gov/legis/Law.aspx?d=89388',
    ],
  },
] as const;

export function jurisdictionRegistryKey(stateFips: string, countyFips: string): string {
  return `${stateFips.padStart(2, '0')}:${countyFips.padStart(3, '0')}`;
}

/** Primary registry index. Bounding boxes only select a FIPS-keyed adapter. */
export const JURISDICTION_REGISTRY_BY_FIPS: Readonly<Record<string, JurisdictionRegistryEntry>> =
  Object.freeze(Object.fromEntries(JURISDICTION_REGISTRY.map(entry => [
    jurisdictionRegistryKey(entry.stateFips, entry.countyFips),
    entry,
  ])));

export function findJurisdictionRegistryEntryByFips(
  stateFips: string,
  countyFips: string,
): JurisdictionRegistryEntry | null {
  return JURISDICTION_REGISTRY_BY_FIPS[jurisdictionRegistryKey(stateFips, countyFips)] ?? null;
}

export const CENSUS_INCORPORATED_PLACES_LAYER: JurisdictionArcgisLayer = {
  providerId: 'us-census-tigerweb-places',
  queryUrl:
    'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/Places_CouSub_ConCity_SubMCD/MapServer/4/query',
  layerKind: 'city-limit',
  nameFields: ['BASENAME', 'NAME'],
  idFields: ['GEOID', 'PLACE'],
  sourceLabel: 'U.S. Census TIGERweb incorporated places (ACS 2025)',
  sourceUrl:
    'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/Places_CouSub_ConCity_SubMCD/MapServer',
};

/** Independent county geometry used to prove a local registry's scope. */
export const CENSUS_COUNTIES_LAYER: JurisdictionArcgisPolygonSource = {
  providerId: 'us-census-tigerweb-counties',
  queryUrl:
    'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/State_County/MapServer/1/query',
  nameFields: ['BASENAME', 'NAME'],
  idFields: ['GEOID'],
  sourceLabel: 'U.S. Census TIGERweb counties (January 1, 2025 vintage)',
  sourceUrl:
    'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/State_County/MapServer',
};

/**
 * Coarse discovery only. A bounding box is not proof that a site is in the
 * registry's county: the deliberately generous Calcasieu pilot envelope also
 * touches adjacent parishes. Callers MUST validate the candidate against an
 * authoritative county FIPS before selecting its local boundary layers.
 */
export function findJurisdictionRegistryCandidate(
  lat: number,
  lng: number,
): JurisdictionRegistryEntry | null {
  return JURISDICTION_REGISTRY.find(entry => (
    lng >= entry.bbox.west
    && lng <= entry.bbox.east
    && lat >= entry.bbox.south
    && lat <= entry.bbox.north
  )) ?? null;
}

/**
 * Select a registry only when both its coarse envelope and independently
 * resolved state/county FIPS agree. Omitting the verified FIPS intentionally
 * returns null so no caller can accidentally restore bbox-only selection.
 */
export function findJurisdictionRegistryEntry(
  lat: number,
  lng: number,
  verifiedFips?: VerifiedJurisdictionFips,
): JurisdictionRegistryEntry | null {
  if (!verifiedFips) return null;
  const resolved = findJurisdictionRegistryEntryByFips(
    verifiedFips.stateFips,
    verifiedFips.countyFips,
  );
  if (!resolved) return null;
  return lng >= resolved.bbox.west
    && lng <= resolved.bbox.east
    && lat >= resolved.bbox.south
    && lat <= resolved.bbox.north
    ? resolved
    : null;
}
